import { debounce } from "lodash";
import { useCallback, useEffect, useRef } from "react";

export interface UseInfiniteScrollProps {
  /**
   * Whether the infinite scroll is enabled.
   * @default true
   */
  isEnabled?: boolean;
  /**
   * Whether there are more items to load. The observer will disconnect when there are no more items to load.
   */
  hasMore?: boolean;
  /**
   * The distance in pixels before the end of the items that will trigger a call to load more.
   * @default 250
   */
  distance?: number;
  /**
   * Use loader element for the scroll detection.
   */
  shouldUseLoader?: boolean;
  /**
   * Callback to load more items.
   */
  onLoadMore?: () => void;
  /**
   * External ref to the scroll container element. If provided, this will be used instead of creating an internal ref.
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  /**
   * External ref to the loader element. If provided, this will be used instead of creating an internal ref.
   */
  loaderRef?: React.RefObject<HTMLElement | null>;
}

export function useInfiniteScroll<
  ScrollContainerElement extends HTMLElement = HTMLElement,
  LoaderElement extends HTMLElement = HTMLElement,
>(props: UseInfiniteScrollProps = {}) {
  const {
    hasMore = true,
    distance = 250,
    isEnabled = true,
    shouldUseLoader = true,
    onLoadMore,
    scrollContainerRef: externalScrollContainerRef = null,
    loaderRef: externalLoaderRef = null,
  } = props;

  const internalScrollContainerRef = useRef<ScrollContainerElement>(null);
  const internalLoaderRef = useRef<LoaderElement>(null);

  // Use external refs if provided, otherwise use internal refs
  const scrollContainerRef =
    (externalScrollContainerRef as React.RefObject<ScrollContainerElement>) ||
    internalScrollContainerRef;
  const loaderRef = (externalLoaderRef as React.RefObject<LoaderElement>) || internalLoaderRef;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);

  const loadMore = useCallback(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!isLoadingRef.current && hasMore && onLoadMore) {
      isLoadingRef.current = true;
      onLoadMore();
      timer = setTimeout(() => {
        isLoadingRef.current = false;
      }, 100); // Debounce time to prevent multiple calls
    }

    return () => clearTimeout(timer);
  }, [hasMore, onLoadMore]);

  useEffect(() => {
    const scrollContainerElement = scrollContainerRef.current;

    if (!isEnabled || !scrollContainerElement || !hasMore) return;

    // If using the loader element, load more content when it comes into view
    if (shouldUseLoader) {
      // Helper to set up the IntersectionObserver on the loader element
      const setupObserver = (loaderElement: HTMLElement) => {
        // Clean up any existing observer first
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        const observer = new IntersectionObserver(
          entries => {
            const [entry] = entries;

            if (entry.isIntersecting) {
              loadMore();
            }
          },
          {
            root: scrollContainerElement,
            rootMargin: `0px 0px ${distance}px 0px`,
            threshold: 0.1,
          }
        );

        observer.observe(loaderElement);
        observerRef.current = observer;
      };

      // Try to set up observer immediately if loader element exists
      // Since we use useEffect (runs after paint), refs should be attached
      const loaderElement = loaderRef.current;
      if (loaderElement) {
        setupObserver(loaderElement);
      }

      // Use MutationObserver to detect when loader element appears/disappears in the DOM
      // This handles cases like accordions where content is unmounted when collapsed
      const mutationObserver = new MutationObserver(() => {
        const currentLoaderElement = loaderRef.current;
        if (currentLoaderElement && !observerRef.current) {
          setupObserver(currentLoaderElement);
        } else if (!currentLoaderElement && observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      });

      mutationObserver.observe(scrollContainerElement, {
        childList: true,
        subtree: true,
      });

      return () => {
        mutationObserver.disconnect();
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      };
    }

    // Otherwise load more content when we're close to the bottom of the scroll container
    const debouncedCheckIfNearBottom = debounce(() => {
      if (
        scrollContainerElement.scrollHeight - scrollContainerElement.scrollTop <=
        scrollContainerElement.clientHeight + distance
      ) {
        loadMore();
      }
    }, 100);

    scrollContainerElement.addEventListener("scroll", debouncedCheckIfNearBottom);

    return () => {
      scrollContainerElement.removeEventListener("scroll", debouncedCheckIfNearBottom);
    };
  }, [hasMore, distance, isEnabled, shouldUseLoader, loadMore, scrollContainerRef, loaderRef]);

  return [loaderRef, scrollContainerRef] as const;
}

export type UseInfiniteScrollReturn = ReturnType<typeof useInfiniteScroll>;
